import React from 'react';
import { ResponsiveImage } from '../ResponsiveImage';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, ChevronRight } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import { ArticleCard } from './ArticleCard';
import { ArticleContent } from './ArticleContent';
import { ArticleCta } from './ArticleCta';
import { useArticleMetadata } from '../../hooks/useArticleMetadata';

export const ArticleDetail = ({ articles, section }) => {
  const { slug } = useParams();
  const basePath = `/conseils-${section}`;
  const label = section === 'carriere' ? 'Conseils carrière' : 'Conseils en recrutement';
  const article = articles.find(item => item.slug === slug);
  const legacyArticle = articles.find(item => String(item.id) === slug);
  useArticleMetadata({
    title: article?.title || 'Article introuvable',
    description: article?.excerpt || 'Cet article n’est pas disponible. Retrouvez tous nos conseils.',
    path: `${basePath}/${slug}`, image: article?.image, notFound: !article, isArticle: Boolean(article),
  });

  if (!article && legacyArticle) return <Navigate to={`${basePath}/${legacyArticle.slug}`} replace />;
  if (!article) return (
    <div className="min-h-screen bg-white">
      <Header />
      <main id="main-content" tabIndex={-1} data-testid="article-not-found" className="max-w-4xl mx-auto px-4 sm:px-6 py-20">
        <p className="text-[#E30022] mb-4" data-testid="article-not-found-code">404</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-gray-800 mb-6" data-testid="article-not-found-title">Article introuvable</h1>
        <p className="text-gray-600 mb-8" data-testid="article-not-found-description">Cet article n’existe pas ou n’est plus disponible.</p>
        <Link to={basePath} data-testid="article-not-found-back-link" className="inline-flex gap-2 items-center text-[#E30022] hover:underline"><ArrowLeft className="h-4 w-4" />Retour aux articles</Link>
      </main>
      <Footer />
    </div>
  );

  const otherArticles = articles.filter(item => item.slug !== article.slug).slice(0, 3);
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <nav aria-label="Fil d’Ariane" data-testid="article-breadcrumb" className="bg-gray-50 border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <Link to="/" data-testid="article-breadcrumb-home-link" className="hover:text-[#E30022]">Accueil</Link>
          <ChevronRight aria-hidden="true" className="h-4 w-4 shrink-0" />
          <Link to={basePath} data-testid="article-breadcrumb-section-link" className="hover:text-[#E30022]">{label}</Link>
          <ChevronRight aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span aria-current="page" data-testid="article-breadcrumb-current" className="text-gray-800 min-w-0 break-words">{article.title}</span>
        </div>
      </nav>
      <main id="main-content" tabIndex={-1}>
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 min-w-0 break-words" data-testid="article-detail">
          <Link to={basePath} data-testid="article-back-link" className="inline-flex items-center gap-2 text-[#E30022] hover:underline mb-6"><ArrowLeft className="h-4 w-4" />Retour aux articles</Link>
          <div><span data-testid="article-category" className="inline-block bg-[#E30022] text-white px-3 py-1 text-sm rounded mb-4">{article.category}</span></div>
          <h1 data-testid="article-title" className="text-4xl sm:text-5xl lg:text-6xl font-serif text-gray-800 mb-6 leading-tight">{article.title}</h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-500 mb-8">
            <span data-testid="article-author" className="flex items-center gap-2"><User aria-hidden="true" className="h-4 w-4 shrink-0" />{article.author}, {article.authorRole}</span>
            <span data-testid="article-date" className="flex items-center gap-2"><Calendar aria-hidden="true" className="h-4 w-4 shrink-0" />{article.date}</span>
            <span data-testid="article-read-time" className="flex items-center gap-2"><Clock aria-hidden="true" className="h-4 w-4 shrink-0" />{article.readTime} de lecture</span>
          </div>
          <div className="aspect-video overflow-hidden rounded-lg mb-10">
            <ResponsiveImage src={article.image} sizes="(min-width: 896px) 832px, 100vw" alt={article.title} data-testid="article-image" className="w-full h-full object-contain" fetchPriority="high" loading="eager" />
          </div>
          <ArticleContent content={article.content} />
        </article>
        <ArticleCta section={section} />
        <section className="bg-gray-50 py-12 sm:py-16" aria-labelledby="related-articles-title">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="related-articles-title" data-testid="related-articles-title" className="text-base md:text-lg font-serif text-gray-800 mb-8">Articles similaires</h2>
            <div data-testid="related-articles" className="grid md:grid-cols-3 gap-8">
              {otherArticles.map(related => <ArticleCard key={related.slug} article={related} section={section} context="related" />)}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};